import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, MapPin, Clock, DollarSign, Coins, Info } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import { Input } from '../../ui/Input';
import { Badge } from '../../ui/Badge';

import {
  CAMPAIGN_DURATION,
  SUPPORTED_CURRENCIES,
  CURRENCY_LIMITS,
  getCurrencySymbol
} from '../../../constants/index.js';

const ProjectStoryStep = ({ formData, updateFormData, errors, setErrors }) => {
  const [charCount, setCharCount] = useState(formData.story?.length || 0);
  const [currencyLimits, setCurrencyLimits] = useState(CURRENCY_LIMITS[formData.currency || 'PI']);

  // Update currency limits when currency changes
  useEffect(() => {
    const currency = formData.currency || 'PI';
    setCurrencyLimits(CURRENCY_LIMITS[currency]);
  }, [formData.currency]);

  // Campaign duration options
  const durationOptions = [
    { value: CAMPAIGN_DURATION.THREE_DAYS, label: '3 Days', description: 'Quick campaign for urgent needs', badge: 'Urgent' },
    { value: CAMPAIGN_DURATION.SEVEN_DAYS, label: '7 Days', description: 'One week campaign', badge: 'Fast' },
    { value: CAMPAIGN_DURATION.TWO_WEEKS, label: '2 Weeks', description: 'Two week campaign', badge: 'Standard' },
    { value: CAMPAIGN_DURATION.FOUR_WEEKS, label: '4 Weeks', description: 'One month campaign', badge: 'Extended' },
    { value: CAMPAIGN_DURATION.SIX_WEEKS, label: '6 Weeks', description: 'Extended campaign', badge: 'Long' },
    { value: CAMPAIGN_DURATION.THREE_MONTHS, label: '3 Months', description: 'Long-term campaign', badge: 'Maximum' }
  ];

  const handleInputChange = (field, value) => {
    updateFormData({ [field]: value });
    
    if (field === 'story') {
      setCharCount(value.length);
    }
  };

  const validateField = (field, value) => {
    const newErrors = { ...errors };

    switch (field) {
      case 'story':
        if (!value || value.length < 100) {
          newErrors.story = 'Story must be at least 100 characters';
        } else if (value.length > 5000) {
          newErrors.story = 'Story cannot exceed 5000 characters';
        } else {
          delete newErrors.story;
        }
        break;

      case 'funding_goal':
        const amount = parseFloat(value);
        const currency = formData.currency || 'PI';
        const limits = CURRENCY_LIMITS[currency];
        const symbol = getCurrencySymbol(currency);
        
        if (!value || isNaN(amount)) {
          newErrors.funding_goal = 'Funding goal is required';
        } else if (amount < limits.MIN_FUNDING) {
          newErrors.funding_goal = `Minimum funding goal is ${symbol} ${limits.MIN_FUNDING.toLocaleString()}`;
        } else if (amount > limits.MAX_FUNDING) {
          newErrors.funding_goal = `Maximum funding goal is ${symbol} ${limits.MAX_FUNDING.toLocaleString()}`;
        } else {
          delete newErrors.funding_goal;
        }
        break;

      case 'country':
        if (!value || value.length < 2) {
          newErrors.country = 'Country is required';
        } else {
          delete newErrors.country;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
  };

  return (
    <div className="space-y-6 max-w-full overflow-hidden px-1 sm:px-0">
      {/* Project Story */}
      <Card className="border-0 shadow-soft hover:shadow-medium transition-all duration-300">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-xl sm:text-h4 font-heading text-neutral-900 flex items-center gap-2">
            📖 Tell Your Story
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div>
            <label className="block text-base sm:text-p1-m text-neutral-900 mb-2">
              Why do you need funding? *
            </label>
            <textarea
              value={formData.story || ''}
              onChange={(e) => handleInputChange('story', e.target.value)}
              onBlur={(e) => validateField('story', e.target.value)}
              placeholder="Share your story... Why do you need this funding? What impact will it have? Be specific about your goals and how the funds will help you achieve them."
              rows={8}
              maxLength={5000}
              className={`w-full px-4 py-3 text-sm sm:text-base border rounded-xl focus:ring-2 focus:ring-primary-teal focus:border-transparent resize-none transition-all duration-200 ${
                errors.story ? 'border-red-500 bg-red-50/30' : 'border-neutral-200 bg-neutral-50/30'
              }`}
            />
            <div className="flex justify-between items-center mt-2 px-1">
              {errors.story ? (
                <div className="flex items-center gap-1.5 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.story}</span>
                </div>
              ) : (
                <p className="text-xs sm:text-p2-r text-neutral-500">
                  Minimum 100 characters.
                </p>
              )}
              <span className={`text-xs sm:text-p2-r font-medium ${charCount > 5000 || charCount < 100 ? 'text-orange-600' : 'text-neutral-500'}`}>
                {charCount}/5000
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Currency Selection */}
      <Card className="border-0 shadow-soft hover:shadow-medium transition-all duration-300">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-xl sm:text-h4 font-heading text-neutral-900 flex items-center gap-2">
            <Coins className="w-5 h-5 text-primary-teal" />
            Select Currency
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div>
            <label className="block text-base sm:text-p1-m text-neutral-900 mb-3">
              Which currency do you want to raise funds in? *
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.values(SUPPORTED_CURRENCIES).map((curr) => (
                <motion.label
                  key={curr.code}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 active:bg-neutral-50 ${
                    formData.currency === curr.code
                      ? 'border-primary-teal bg-primary-teal/5 ring-2 ring-primary-teal/10'
                      : 'border-neutral-200 hover:border-primary-teal/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="currency"
                    value={curr.code}
                    checked={formData.currency === curr.code}
                    onChange={(e) => {
                      handleInputChange('currency', e.target.value);
                      handleInputChange('funding_goal', '');
                    }}
                    className="w-5 h-5 text-primary-teal"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl flex-shrink-0">{curr.symbol}</span>
                      <div className="overflow-hidden">
                        <div className="font-semibold text-neutral-900 text-sm sm:text-base truncate">{curr.name}</div>
                        <div className="text-xs sm:text-p2-r text-neutral-500">
                          {curr.code === 'PI' ? 'Pi Network' : 'Nigerian Naira'}
                        </div>
                      </div>
                    </div>
                  </div>
                  {curr.code === 'NGN' && (
                    <Badge variant="secondary" className="text-[10px] h-5 bg-orange-100 text-orange-700 border-0">
                      New
                    </Badge>
                  )}
                </motion.label>
              ))}
            </div>
            {errors.currency && (
              <div className="flex items-center gap-1.5 text-red-600 mt-2 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.currency}</span>
              </div>
            )}
            <div className="mt-4 p-3 bg-blue-50/50 border border-blue-100 rounded-xl">
              <p className="text-xs sm:text-p2-r text-blue-800 flex items-center gap-2">
                <Info className="w-3.5 h-3.5 flex-shrink-0" />
                <span>
                  <strong>Range:</strong> {getCurrencySymbol(formData.currency || 'PI')}{currencyLimits.MIN_FUNDING.toLocaleString()} - {getCurrencySymbol(formData.currency || 'PI')}{currencyLimits.MAX_FUNDING.toLocaleString()}
                </span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Funding Goal */}
      <Card className="border-0 shadow-soft hover:shadow-medium transition-all duration-300">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-xl sm:text-h4 font-heading text-neutral-900 flex items-center gap-2">
            💰 Funding Goal
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div>
            <label className="block text-base sm:text-p1-m text-neutral-900 mb-2">
              How much do you need to raise? *
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 text-lg font-semibold">
                {getCurrencySymbol(formData.currency || 'PI')}
              </div>
              <Input
                type="text"
                inputMode="decimal"
                value={formData.funding_goal || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  const parts = value.split('.');
                  const cleanValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                  handleInputChange('funding_goal', cleanValue);
                }}
                onBlur={(e) => validateField('funding_goal', e.target.value)}
                placeholder={formData.currency === 'NGN' ? '1,000,000' : '1,000'}
                className={`pl-12 h-11 sm:h-12 text-lg sm:text-xl font-medium tracking-tight ${errors.funding_goal ? 'border-red-500 bg-red-50/30' : 'bg-neutral-50/30'}`}
              />
            </div>
            {errors.funding_goal ? (
              <div className="flex items-center gap-1.5 text-red-600 mt-2 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.funding_goal}</span>
              </div>
            ) : (
              <p className="text-xs sm:text-p2-r text-neutral-500 mt-2">
                Realistically set between {getCurrencySymbol(formData.currency || 'PI')}{currencyLimits.MIN_FUNDING.toLocaleString()} and {getCurrencySymbol(formData.currency || 'PI')}{currencyLimits.MAX_FUNDING.toLocaleString()}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Campaign Duration */}
      <Card className="border-0 shadow-soft hover:shadow-medium transition-all duration-300">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-xl sm:text-h4 font-heading text-neutral-900 flex items-center gap-2">
            ⏰ Campaign Duration
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div>
            <label className="block text-base sm:text-p1-m text-neutral-900 mb-3">
              How long should your campaign run? *
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
              {durationOptions.map((option) => (
                <motion.label
                  key={option.value}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className={`flex flex-col p-3 sm:p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 active:bg-neutral-50 ${
                    formData.campaign_duration === option.value
                      ? 'border-primary-teal bg-primary-teal/5'
                      : 'border-neutral-200 hover:border-primary-teal/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="campaign_duration"
                    value={option.value}
                    checked={formData.campaign_duration === option.value}
                    onChange={(e) => handleInputChange('campaign_duration', e.target.value)}
                    className="sr-only"
                  />
                  <div className="flex items-center justify-between mb-1.5 sm:mb-2 gap-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Clock className="w-3.5 h-3.5 text-primary-teal flex-shrink-0" />
                      <span className="font-semibold text-neutral-900 text-xs sm:text-sm truncate">{option.label}</span>
                    </div>
                  </div>
                  <p className="text-[10px] sm:text-xs text-neutral-500 leading-tight flex-1 mb-2">{option.description}</p>
                  <div className="mt-auto">
                    <Badge 
                      variant={option.badge === 'Urgent' ? 'destructive' : 'secondary'}
                      className="text-[9px] sm:text-[10px] px-1.5 py-0 h-4 border-0"
                    >
                      {option.badge}
                    </Badge>
                  </div>
                </motion.label>
              ))}
            </div>

            {errors.campaign_duration && (
              <div className="flex items-center gap-1.5 text-red-600 mt-2 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.campaign_duration}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Location */}
      <Card className="border-0 shadow-soft hover:shadow-medium transition-all duration-300">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-xl sm:text-h4 font-heading text-neutral-900 flex items-center gap-2">
            📍 Project Location
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Country */}
            <div>
              <label className="block text-sm sm:text-p1-m font-medium text-neutral-900 mb-2">
                Country *
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <Input
                  type="text"
                  value={formData.country || ''}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  onBlur={(e) => validateField('country', e.target.value)}
                  placeholder="e.g., Nigeria"
                  className={`pl-10 h-11 sm:h-12 ${errors.country ? 'border-red-500 bg-red-50/30' : 'bg-neutral-50/30'}`}
                />
              </div>
              {errors.country && (
                <div className="flex items-center gap-1.5 text-red-600 mt-2 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.country}</span>
                </div>
              )}
            </div>

            {/* Region/State */}
            <div>
              <label className="block text-sm sm:text-p1-m font-medium text-neutral-900 mb-2">
                Region/State <span className="text-neutral-500 font-normal">(Optional)</span>
              </label>
              <Input
                type="text"
                value={formData.region || ''}
                onChange={(e) => handleInputChange('region', e.target.value)}
                placeholder="e.g., Lagos State"
                maxLength={100}
                className="h-11 sm:h-12 bg-neutral-50/30"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Banner */}
      <Card className="bg-green-50 border-green-200 border-2">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Coins className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="text-p2-r text-green-800">
              <p className="font-semibold mb-1">Funding Tips</p>
              <ul className="space-y-1 text-sm">
                <li>• Be specific about how you'll use the funds</li>
                <li>• Set a realistic and achievable goal</li>
                <li>• Tell a compelling story that connects with donors</li>
                <li>• Choose an appropriate campaign duration</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectStoryStep;
